import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { verifyEmail, resendVerification } from "@/services/auth-service";
import { ConfirmDialog } from "@/components/common";
import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";
import icCheck from "@/assets/icons/ic-check.svg";
import icMailStroke from "@/assets/icons/ic-mail-stroke.svg";
import icWarningTriangle from "@/assets/icons/ic-warning-triangle.svg";

const COOLDOWN_SECONDS = 179;

export const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";

  const [verifying, setVerifying] = useState(!!token);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showResentDialog, setShowResentDialog] = useState(false);
  const [showExpiredDialog, setShowExpiredDialog] = useState(false);

  const verifiedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startCooldown = useCallback(
    (existingEndTime?: number) => {
      const endTime = existingEndTime || Date.now() + COOLDOWN_SECONDS * 1000;
      if (!existingEndTime) {
        localStorage.setItem(`verificationCooldown_${email}`, endTime.toString());
      }

      if (timerRef.current) clearTimeout(timerRef.current);

      const tick = () => {
        const remaining = Math.ceil((endTime - Date.now()) / 1000);
        if (remaining <= 0) {
          setCooldown(0);
          timerRef.current = null;
          localStorage.removeItem(`verificationCooldown_${email}`);
          return;
        }
        setCooldown(remaining);
        timerRef.current = setTimeout(tick, 1000);
      };
      tick();
    },
    [email],
  );

  useEffect(() => {
    if (!token) {
      const savedTime = localStorage.getItem(`verificationCooldown_${email}`);
      const endTime = savedTime ? parseInt(savedTime, 10) : null;

      if (endTime && endTime > Date.now()) {
        startCooldown(endTime);
      } else {
        startCooldown();
      }
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [email, token, startCooldown]);

  useEffect(() => {
    if (!token || verifiedRef.current) return;
    verifiedRef.current = true;
    verifyEmail(token)
      .then(() => setShowSuccessDialog(true))
      .catch(() => setShowExpiredDialog(true))
      .finally(() => setVerifying(false));
  }, [token]);

  const handleResend = async () => {
    if (!email || resending || cooldown > 0) return;
    setResending(true);
    try {
      await resendVerification(email);
      setShowExpiredDialog(false);
      setShowResentDialog(true);
      startCooldown();
    } catch {
      // silent fail
    } finally {
      setResending(false);
    }
  };

  const formatCooldown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const isButtonDisabled = resending || cooldown > 0;

  if (verifying) {
    return (
      <div className="flex flex-col items-center justify-center px-5 py-10 md:px-8 md:py-[100px]">
        <p className="text-base text-content-medium">이메일 인증 중...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center px-5 py-10 md:px-8 md:py-[100px]">
      <div className="w-full md:max-w-[608px] flex flex-col gap-12">
        <div className="flex flex-col gap-6">
          <button
            type="button"
            onClick={() => navigate("/signup")}
            className="w-12 h-12 flex items-center justify-center"
            aria-label="뒤로 가기"
          >
            <img src={icArrowLeft} alt="" width={24} height={24} />
          </button>

          <div className="flex flex-col gap-4">
            <h1 className="text-[30px] leading-9 tracking-[-0.02em] md:text-[32px] md:leading-10 md:tracking-[-0.03em] font-normal text-content-strong">
              이메일 인증
            </h1>
            <p className="text-base leading-6 tracking-[-0.01em] text-content-medium">
              <span className="text-content-strong">{email}</span> 으로 발송된 인증 이메일을 확인해
              주세요. 이메일 인증을 완료하려면 메일에 포함된 링크를 클릭해 주세요. 만약 인증
              이메일을 받지 못하셨다면, 아래의 &apos;재전송&apos; 버튼을 클릭해 주세요.
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-base leading-6 tracking-[-0.01em] font-bold text-content-strong">
              이메일을 받지 못하셨다면 다음 사항을 확인해 주세요:
            </p>
            <ul className="list-disc pl-5 text-base leading-6 tracking-[-0.01em] text-content-strong space-y-1">
              <li>입력하신 이메일 주소가 정확한지 확인해 주세요.</li>
              <li>스팸 메일함 또는 기타 편지함을 확인해 주세요.</li>
              <li>이메일 수신 허용 리스트(화이트리스트)를 설정해 주세요.</li>
              <li>이메일 클라이언트가 정상적으로 작동하는지 확인해 주세요.</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={handleResend}
            disabled={isButtonDisabled}
            className="h-12 w-full text-base leading-6 tracking-[-0.01em] font-bold  transition-colors bg-disabled text-white disabled:cursor-not-allowed enabled:bg-primary enabled:hover:bg-primary/90"
          >
            {resending ? "전송 중..." : "재전송"}
          </button>

          {cooldown > 0 && (
            <p className="text-base leading-6 tracking-[-0.01em] text-content-medium">
              버튼은 <span className="font-bold text-primary">{formatCooldown(cooldown)}</span> 후에
              활성화됩니다
            </p>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        iconVariant="success"
        icon={<img src={icCheck} alt="" className="w-8 h-8 invert brightness-0" />}
        title="회원가입을 축하합니다!"
        description="이메일 인증이 완료되었습니다."
        primaryAction={{
          label: "OK",
          onClick: () => navigate("/login", { replace: true }),
        }}
      />

      {/* Email Resent Dialog */}
      <ConfirmDialog
        open={showResentDialog}
        onOpenChange={setShowResentDialog}
        iconVariant="info"
        icon={<img src={icMailStroke} alt="" width={24} height={24} />}
        title="이메일을 확인해주세요"
        description={`${email}으로 인증 메일을 재발송했습니다.`}
        primaryAction={{
          label: "OK",
          onClick: () => setShowResentDialog(false),
        }}
      />

      {/* Link Expired Dialog */}
      <ConfirmDialog
        open={showExpiredDialog}
        onOpenChange={setShowExpiredDialog}
        iconVariant="warning"
        icon={<img src={icWarningTriangle} alt="" width={32} height={32} />}
        title="Oops!"
        description="인증 링크가 유효하지 않거나 만료되었습니다."
        secondaryAction={{
          label: "Cancel",
          onClick: () => setShowExpiredDialog(false),
        }}
        primaryAction={{
          label: "재 전송",
          onClick: handleResend,
        }}
      />
    </div>
  );
};
